<template>
    <md-steppers class="steperBox" :md-active-step.sync="stepper.active" md-linear @md-changed="lockSteps"
        v-if="campaign && currentOrder">
        <md-step id="step1" :md-label="$t('views.order.product.steps.step1')" :md-done.sync="stepper.done.step1" ref="step1">
            <OrderProductStep1 v-model="form.step1"
                               @nextStep="goToNextStep('step2')"
                               :products="products"
                               :current-order="currentOrder"
                               :order-type="orderType"
                               @cancel="cancel" />
        </md-step>
        <md-step id="step2" :md-label="$t('views.order.product.steps.step2')" :md-done.sync="stepper.done.step2" ref="step2">
            <OrderProductStep2 v-model="form.step2"
                               :order-stations="orderStations"      
                               :order-no="orderNo"
                               :selected-product="selectedProduct"
                               :order-products="orderProducts"
                               :current-order="currentOrder"
                               :max-product-share="maxProductShare"
                               :order-type="orderType"
                               @nextStep="goToNextStep('step3')"
                               @cancel="cancel" />
        </md-step>
        <md-step id="step3" :md-label="$t('views.order.product.steps.step3')" :md-done.sync="stepper.done.step3" ref="step3">
            <OrderProductStep3 v-model="form.step3"
                               :dates-range="getDatesRange()"
                               :current-order="currentOrder"
                               :edit-mode="!!productNo"
                               @nextStep="createOrderProduct"
                               @cancel="cancel" />
        </md-step>
    </md-steppers>
</template>

<script>
    import serverSideValidationErrors from '@/mixins/serverSideValidationErrors';
    import OrderProductStep2 from '@/views/order/product/OrderProductStep2';
    import OrderProductStep1 from '@/views/order/product/OrderProductStep1';
    import OrderProductStep3 from '@/views/order/product/OrderProductStep3';
    import {mapActions, mapGetters} from 'vuex';
    import {
        campaignsEndpoint,
        conditionsEndpoint,
        orderProductsEndpoint,
        ordersEndpoint,
        productsEndpoint,
        stationsEndpoint
    } from '@/services/adsign-backend/endpoints';
import { orderStationsEndpoint } from '../../../services/adsign-backend/endpoints';

    export default {
        name: 'OrderProductForm',
        components: {OrderProductStep3, OrderProductStep1, OrderProductStep2},
        mixins: [serverSideValidationErrors],
        props: {
            orderId: {
            type: [String, Number],
            default: null
            },
            orderNo: {
                type: String,
                required: true
            },
            productNo: {
                type: String,
                default: null
            },
            backUrl: {
                type: String,
                default: null
            },
            draft: {
                type: Boolean,
                required: true
            }
        },
        computed: {
            selectedProduct() {
                return this.products.find(value => value.id == this.form.step1.productId)
            },
            maxProductShare: {
                get() {
                    return this.orderProducts
                        .filter(orderProduct => orderProduct.id != this.productNo)
                        .reduce((share, item) => share - Math.round(item.productShare * 100), 100);

                }
            },
            orderType() {
                return this.currentOrder?.orderType?.name;
            }
        },
        data: function() {
            return {
                orderStations: null,
                campaign: null,
                currentOrder: null,
                currentOrderProduct: null,
                products: [],
                orderProducts: [],
                steps: ['step1', 'step2', 'step3'],
                sending: false,
                lastProductNo: 0,
                stepper: {
                    active: 'step1',
                    done: {
                        step1: false,
                        step2: false,
                        step3: false
                    }
                },
                form: {
                    id: this.productNo,
                    step1: {
                        productId: null,
                        description: null,
                        duration: null,
                        serTypeId: null,
                        genreId: null,
                        cappingId: null,
                        blockTypeId: null,
                        platformId: null
                    },
                    step2: {
                        productShare: 1,
                        positionShare: 0,
                        positions: [],
                        primeTimeShare: 0,
                        beerFee: false
                    },
                    step3: {
                        productTimeIntervals: []
                    }
                }
            }
        },
        methods: {
            lockSteps(currentStep) {
                const stepIndex = this.steps.findIndex(step => step == currentStep);
                this.steps.slice(stepIndex).forEach((step) => this.stepper.done[step] = false);
            },
            getDatesRange() {
                return {
                    min: this.$moment(this.campaign.validFrom).format('L'),
                    max: this.$moment(this.campaign.validTo).format('L')
                }
            },
            goToNextStep(nextStep) {
                this.stepper.active = nextStep;
            },
            createOrderProduct() {
                this.saveOrUpdateOrderProduct().then(() => {
                    this.$router.push(this.backUrl);
                }) ;
            },
            saveOrUpdateOrderProduct() {
                const orderProduct = {
                    productId: this.form.step1.productId,
                    duration: Number(this.form.step1.duration),
                    description: this.form.step1.description,
                    ...(this.form.step1.isInternetOrOohOrder && {
                        serType: { valueId: this.form.step1.serTypeId },
                        genre: { valueId: this.form.step1.genreId },
                        capping: { valueId: this.form.step1.cappingId },
                        blockType: { valueId: this.form.step1.blockTypeId },
                        platformType: { valueId: this.form.step1.platformId}
                    }),
                    primeTimeShare: this.form.step2.primeTimeShare / 100,
                    positionShare: this.form.step2.positionShare / 100,
                    productShare: this.form.step2.productShare / 100,
                    positions: this.form.step2.positions.map(position => ({
                        valueId: position
                    })),
                    timePreferences: this.form.step3.productTimeIntervals.map(interval => ({
                        validFrom: this.$moment(interval.validFrom, 'L').format('YYYY-MM-DD'),
                        validTo: this.$moment(interval.validTo, 'L').format('YYYY-MM-DD')
                    }))
                };

                if (this.form.id) {
                    return orderProductsEndpoint.updateOrderProduct(this.currentOrder.id, this.form.id, orderProduct, this.draft)
                        .then(result => result.data.id);
                } else {
                    return orderProductsEndpoint.createOrderProduct(this.currentOrder.id, orderProduct, this.draft)
                        .then(result => result.data.id);
                }
            },
            cancel() {
                this.$router.push(this.backUrl);
            }
        },
        mounted() {
            ordersEndpoint.getOrder(this.orderNo).then(orderResult => {
                this.currentOrder = orderResult.data;
                orderStationsEndpoint.getOrderStations(this.currentOrder.id).then(result => this.orderStations = result.data);
                campaignsEndpoint.getCampaign(this.currentOrder.campaignId).then(campaignResult => {
                    this.campaign = campaignResult.data;
                    if (!this.productNo) {
                        this.form.step1.description = campaignResult.data.name;
                    }

                    productsEndpoint.getProductsList(campaignResult.data.client.id, orderResult.data.spotType.valueId,
                        campaignResult.data.agency.id).then(result => this.products.push(...result.data));
                });
            });
            orderProductsEndpoint.getOrderProducts(this.orderNo).then(result => this.orderProducts = result.data);



            if (this.productNo) {
                orderProductsEndpoint.getOrderProduct(this.orderNo, this.productNo)
                    .then(result => {
                        this.form = {
                            id: this.productNo,
                            step1: {
                                productId: result.data.productId,
                                description: result.data.description,
                                duration: result.data.duration,
                                serTypeId: result.data.serType?.valueId,
                                genreId: result.data.genre?.valueId,
                                cappingId: result.data.capping?.valueId,
                                blockTypeId: result.data.blockType?.valueId,
                                platformId: result.data.platformType?.valueId
                            },
                            step2: {
                                productShare: Math.round(result.data.productShare * 100),
                                positionShare: Math.round(result.data.positionShare * 100),
                                primeTimeShare: Math.round(result.data.primeTimeShare * 100),
                                positions: result.data.positions.map(item => item.position.valueId),
                            },
                            step3: {
                                productTimeIntervals: result.data.timePreferences.map(tp => ({
                                    validFrom: this.$moment(tp.validFrom).format('L'),
                                    validTo: this.$moment(tp.validTo).format('L')
                                }))
                            }
                        }
                    });
            }
        }
    }
</script>

<style scoped lang="scss">
    .vue-slider-process {
        background-color: #E51937;
    }
    .steperBox{
        margin: 0
    }
</style>
